import type { ReactNode } from "react"
import { useNavigate } from "react-router-dom"
import GridBackdrop from "@/components/GridBackdrop/GridBackdrop"
import LokaMark from "@/components/LokaMark/LokaMark"
import TopNav from "@/components/TopNav/TopNav"
import { useFacilitatorIndex } from "@/hooks/useFacilitatorIndex"
import { ROUTES } from "@/routes/routes"
import type { FacilitatorIndex } from "@/hooks/useFacilitatorIndex"
import styles from "@/styles/components/AppShell.module.css"

/**
 * Every facilitator screen: the mark on the left, the nav pill on the right, the
 * grid behind, and a page column between them.
 *
 * The shell owns the customer and session lists because the nav's two menus and
 * the search both read them, and a screen that needed the same data would
 * otherwise load it twice. Screens that DO need it take it back through
 * `children` as a render prop rather than loading their own copy.
 *
 * There is no bar. The two pieces of chrome float over the page and the content
 * scrolls under nothing, which is why the grid backdrop can run to the top edge.
 */

interface AppShellProps {
  /** Given the lists the nav already loaded, so a screen need not re-read them. */
  children: ReactNode | ((index: FacilitatorIndex) => ReactNode)
  hideNav?: boolean
}

export default function AppShell({ children, hideNav = false }: AppShellProps) {
  const navigate = useNavigate()
  const index = useFacilitatorIndex()

  return (
    <div className={styles.shell}>
      <GridBackdrop />

      <header className={styles.chrome}>
        <button
          type="button"
          className={styles.home}
          onClick={() => navigate(ROUTES.ROOT)}
          aria-label="Loka AI Cards — back to customers"
        >
          <LokaMark className={styles.mark} />
        </button>
        {hideNav ? null : <TopNav index={index} />}
      </header>

      <main className={styles.page}>
        {typeof children === "function" ? children(index) : children}
      </main>
    </div>
  )
}
